import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import LoadingSpinner from "../common/loading";
import UpsertWidgetDialog from "./upsert-widget-dialog";
import UpsertDeviceDialog from "./upsert-device-dialog";
import { useWebSocket } from "../ws/websocket";
import { useEventEmitter } from "../ws/event-context";
import {
  deleteCard,
  updateCanvasCards,
  updateCardLayout,
  updateCardContent,
  upsertWidget,
  copyCardToCanvas,
  batchDeleteCard,
} from "./dashboard-service";
import { useToast } from "../common/toast";
import { useBackdrop } from "../common/backdrop";
import AddWidgetCard from "./add-widget-card";
import useApiCall from "../common/api-call";
import {
  Paper,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DashboardCard from "./dashboard-card";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import AddIcon from "@mui/icons-material/Add";
import EditCardDialog from "./edit-card-dialog";
import DeleteCardDialog, { BatchDeleteDialog } from "./delete-card-dialog";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import CloseIcon from "@mui/icons-material/Close";
// import TwinTagDialog1 from "./twin-widget/twinTag-dialog-1";
import AddOptionDialog from "./add-option-dialog";
import { updateWidgetData } from "./dashboard-service";
import { listCanvas } from "./dashboard-service";
import { copyCard } from "./dashboard-service";
import EditWidgetModal from "./edit-widget-modal";
import { ContentCopy } from "@mui/icons-material";
import GridOnIcon from "@mui/icons-material/GridOn";
import { registerDataWidgets } from "./widget-registry";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import debounce from "lodash/debounce";
import CardContentEditDialog from "./card-content-edit-dialog";
import { isEqual } from "lodash";
import { useDragDrop } from "./drag-context";
import { Typography } from "@mui/material";
import { createGlobalStyle } from "styled-components";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import { getCardColor } from "../common/colors";

const GlobalStyle = createGlobalStyle`
  .react-grid-item {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-layout {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-item > .react-resizable-handle {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-item.react-grid-placeholder {
    transition: none !important;
    animation: none !important;
  }
  
  .widget-container, 
  .widget-container *,
  .MuiBox-root,
  .MuiPaper-root,
  .MuiTypography-root,
  .MuiButton-root,
  .MuiSlider-root,
  .MuiTextField-root,
  .MuiInputBase-root,
  .MuiOutlinedInput-root,
  .MuiInputLabel-root,
  .MuiFormControl-root,
  .MuiSelect-root,
  .MuiMenuItem-root,
  .MuiCheckbox-root,
  .MuiRadio-root,
  .MuiSwitch-root,
  .MuiIconButton-root,
  .MuiSvgIcon-root {
    transition: none !important;
    animation: none !important;
  }
  
  .MuiButton-root:hover,
  .MuiIconButton-root:hover,
  .MuiSlider-thumb:hover,
  .MuiMenuItem-root:hover {
    transition: none !important;
    animation: none !important;
  }

  /* Add these new styles */
  .card-title-trigger,
  .automation-title-trigger {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    -webkit-touch-callout: none !important;
  }
`;

const ResponsiveGridLayout = WidthProvider(Responsive);

const generateLayouts = (cardData, cols, layoutSize) => {
  let nextY = 0;
  let nextX = 0;

  return cardData.map((card) => {
    let { w, h, x, y } = card.layouts[layoutSize] || {};

    if (x === null || y === null || x === undefined || y === undefined) {
      // Auto-calculate x and y for missing ones
      if (nextX + w > cols) {
        nextX = 0;
        nextY += h; // Move to next row if space is not enough
      }
      x = nextX;
      y = nextY;
      nextX += w;
    } else {
      // If x and y are provided, move nextX and nextY accordingly
      nextX = Math.max(nextX, x + w);
      nextY = Math.max(nextY, y);
    }

    return { i: card.id, x, y, w, h };
  });
};

const DashboardView = forwardRef(
  ({ drag = false, dragClose = () => {} }, ref) => {
    const { openBackdrop, closeBackdrop } = useBackdrop();
    const [userInfo, setUserInfo] = useRecoilState(userInfoState);
    const { ws, sendAndWaitWs, sendAndForgetWs } = useWebSocket();
    const { subscribe } = useEventEmitter();
    const [currentWidgets, setCurrentWidgets] = useState([]);
    const [currentCard, setCurrentCard] = useState(null);
    const [currentEditCard, setCurrentEditCard] = useState(null);
    const { successToast, errorToast } = useToast();
    const [layouts, setLayouts] = useState(null);
    const [edit, setEdit] = useState(null);
    const tabsContainerRef = useRef(null);

    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [showContent, setShowContent] = useState(false);
    const [contentLoading, setContentLoading] = useState(false);
    const widget2CardRef = useRef(new Map());
    const cardMapRef = useRef(new Map());
    const widgetMapRef = useRef(new Map());
    const [cardList, setCardList] = useState([]);
    const revisionRef = useRef(-1);
    const curCanvasRef = useRef("");
    const skipEvent = useRef(false);
    const [layoutChanged, setLayoutChanged] = useState(false);
    const [openOptionDialog, setOpenOptionDialog] = useState(false);
    const [openWidgetDialog, setOpenWidgetDialog] = useState(false);
    const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
    const [openCardDialog, setOpenCardDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [widgetNames, setWidgetNames] = useState([]);
    const [isUpsertingWidget, setIsUpsertingWidget] = useState(false);
    const history = useHistory();
    const theme = useTheme();
    const { apiCall } = useApiCall();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [canvasList, setCanvasList] = useState([]);
    const [isCanvasListLoaded, setIsCanvasListLoaded] = useState(false);
    const canvasListLoadingRef = useRef(false);
    const [originalLayouts, setOriginalLayouts] = useState([]);
    const [pressingCardId, setPressingCardId] = useState(null);
    const [isLongPress, setIsLongPress] = useState(false);
    const longPressTimerRef = useRef(null);
    const mouseDownRef = useRef(false);
    const touchStartPositionRef = useRef(null);
    const movementThreshold = 30; // pixels
    const [editCard, setEditCard] = useState(null);
    const [editCardLayout, setEditCardLayout] = useState(false);
    const [isEditTitle, setIsEditTitle] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const isInitialized = useRef(false);
    const latestWidgetValues = useRef({});
    const { draggedCard, setDraggedCard } = useDragDrop();

    const [selectedCards, setSelectedCards] = useState([]);
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    const [hoveredTab, setHoveredTab] = useState(null);

    const isMovingCardRef = useRef(false);

    const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] =
      useState(false);

    const autoScrollIntervalRef = useRef(null);
    const containerRef = useRef(null);

    const handleDragMove = (e) => {
      if (!draggedCard) return;

      e.preventDefault();

      const touch = e.touches[0];
      const tabs = document.querySelectorAll(".canvas-tab");

      tabs.forEach((tab) => tab.classList.remove("drag-hover"));
      setHoveredTab(null);

      const draggedElement = document.querySelector(
        ".react-grid-item.react-draggable-dragging"
      );
      if (draggedElement) {
        draggedElement.style.visibility = "hidden";
      }

      const elementAtPoint = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );

      if (draggedElement) {
        draggedElement.style.visibility = "visible";
      }

      const canvasTab = elementAtPoint?.closest(".canvas-tab");

      if (canvasTab) {
        const rect = canvasTab.getBoundingClientRect();
        canvasTab.classList.add("drag-hover");
        const canvasName = canvasTab.getAttribute("data-canvas-name");
        setHoveredTab({
          name: canvasName,
          x: rect.left + rect.width / 2,
          y: rect.bottom + 10,
        });
      }
    };

    const handleDragEnd = (e) => {
      if (!draggedCard) return;

      const touch = e.changedTouches[0];
      const endElementAtPoint = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      const endCanvasTab = endElementAtPoint?.closest(".canvas-tab");

      if (endCanvasTab) {
        const targetCanvasId = endCanvasTab.getAttribute("data-canvas-id");
        const targetCanvasName = endCanvasTab.getAttribute("data-canvas-name");

        if (targetCanvasId && targetCanvasId !== curCanvasRef.current) {
          isMovingCardRef.current = true;
          setTimeout(() => {
            moveCardToCanvas(
              draggedCard.card,
              targetCanvasId,
              targetCanvasName
            );
          }, 0);
        }
      }

      document.querySelectorAll(".canvas-tab").forEach((tab) => {
        tab.classList.remove("drag-hover");
      });
      setHoveredTab(null);
      setDraggedCard(null);
    };

    useEffect(() => {
      if (!draggedCard) return;

      document.addEventListener("touchmove", handleDragMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleDragEnd);
      document.addEventListener("touchcancel", handleDragEnd);

      const handleMouseMove = (e) => {
        if (!draggedCard) return;

        const tabs = document.querySelectorAll(".canvas-tab");
        tabs.forEach((tab) => tab.classList.remove("drag-hover"));
        setHoveredTab(null);

        tabs.forEach((tab) => {
          const rect = tab.getBoundingClientRect();
          if (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            tab.classList.add("drag-hover");
            const canvasName = tab.getAttribute("data-canvas-name");
            setHoveredTab({
              name: canvasName,
              x: rect.left + rect.width / 2,
              y: rect.bottom + 10,
            });
          }
        });
      };

      const handleMouseUp = (e) => {
        let droppedOnCanvas = false;
        const canvasTabs = document.querySelectorAll(".canvas-tab");

        for (const tab of canvasTabs) {
          const rect = tab.getBoundingClientRect();
          if (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            const targetCanvasId = tab.getAttribute("data-canvas-id");
            const targetCanvasName = tab.getAttribute("data-canvas-name");

            if (targetCanvasId && targetCanvasId !== curCanvasRef.current) {
              isMovingCardRef.current = true;
              setTimeout(() => {
                moveCardToCanvas(
                  draggedCard.card,
                  targetCanvasId,
                  targetCanvasName
                );
              }, 0);
              droppedOnCanvas = true;
            }
            break;
          }
        }

        document.querySelectorAll(".canvas-tab").forEach((tab) => {
          tab.classList.remove("drag-hover");
        });
        setHoveredTab(null);

        const cards = document.querySelectorAll(".react-grid-item");
        cards.forEach((card) => {
          card.style.opacity = "";
          card.style.boxShadow = "";
          card.style.zIndex = "";
        });

        if (!droppedOnCanvas) {
          setDraggedCard(null);
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("touchmove", handleDragMove);
        document.removeEventListener("touchend", handleDragEnd);
        document.removeEventListener("touchcancel", handleDragEnd);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [draggedCard, curCanvasRef.current]);

    useEffect(() => {
      if (!layouts?.length) return;

      if (edit !== null) {
        setOriginalLayouts(layouts);
        // Remove height modification when entering edit mode
        setLayouts(layouts);
        setCardList(cardList);
      } else {
        const newLayouts = layouts.map((layout) => {
          const originalLayout = originalLayouts.find((l) => l.i === layout.i);
          if (originalLayout && layout.h > originalLayout.h) {
            return {
              ...layout,
              h: layout.h - 1,
            };
          }
          return layout;
        });
        setLayouts(newLayouts);

        const updatedCardList = cardList.map((card) => {
          const cl = card.layouts[getCurrentLayoutSize()];
          if (cl && cl.h > cl.h - 2) {
            return {
              ...card,
              layouts: {
                ...card.layouts,
                [getCurrentLayoutSize()]: {
                  ...cl,
                  h: cl.h - 2,
                },
              },
            };
          }
          return card;
        });
        setCardList(updatedCardList);
      }
    }, [edit]);

    useEffect(() => {
      if (!dashboardLoading) {
        setShowContent(true);
      }
    }, [dashboardLoading]);

    useEffect(() => {
      const { unsubscribe } = subscribe((target, data) => {
        if (target === "_dashboard_") {
          if (data.command === "add") {
            handleOpenOptionDialog();
          }
        }
      });
      return () => {
        unsubscribe();
      };
    }, []);

    useEffect(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const canvasId = searchParams.get("canvas");
      if (canvasId) {
        curCanvasRef.current = canvasId;
      }
      const { unsubscribe } = subscribe((target, data) => {
        if (skipEvent.current) {
          return;
        }
        if (target === "/ui/widget/update") {
          const payload = JSON.parse(data.payload);

          const widgets = [];
          for (let change of payload.changes) {
            if (change.type === "UPSERT") {
              widgets.push(change.widget);
            }
          }
          if (widgets.length > 0) {
            handleWidgetUpdateEvent(widgets);
          }
        } else if (target === "/ui/canvas/update") {
          const change = JSON.parse(data.payload);
          console.log(change);
          if (
            change.type === "UPDATE_CANVAS" &&
            change.canvasId === curCanvasRef.current
          ) {
            handleCanvasUpdateEvent(change.revision);
          }
        } else if (target === "/ui/card/update") {
          const payload = JSON.parse(data.payload);
          for (let change of payload.changes) {
            if (change.type === "UPSERT") {
              handleCardUpdateEvent(change.card);
            }
          }
        } else if (target === "/automation/runningStatus/update") {
          const payload = JSON.parse(data.payload);
          if (payload) {
            handleAutomationRunningEvent(payload);
          }
        }
      });
      return () => {
        unsubscribe();
      };
    }, []);

    useEffect(() => {
      if (layoutChanged && !drag) {
        if (!isMovingCardRef.current) {
          handleUpdateCardLayout();
        }
        setLayoutChanged(false);
      }
      if (!drag) {
        isMovingCardRef.current = false;
      }
    }, [drag]);

    const handleCanvasUpdateEvent = (lr) => {
      if (revisionRef.current === -1) {
        setTimeout(() => {
          handleCanvasUpdateEvent(lr);
        }, 500);
      } else {
        if (revisionRef.current !== lr) {
          setTimeout(() => {
            if (revisionRef.current !== lr) {
              openBackdrop("Canvas updated");
            }
          }, 500);
        }
      }
    };

    const handleWidgetUpdateEvent = (widgets) => {
      if (!isInitialized.current) {
        console.log("Dashboard not initialized yet, ignoring widget update");
        return;
      }

      const widgetsArray = Array.isArray(widgets) ? widgets : [widgets];
      let hasUpdates = false;

      for (const widget of widgetsArray) {
        let wid = widget.id;

        if (!widget2CardRef.current.get(wid)) {
          // not related widget, ignore
          continue;
        }

        let nw = widget;
        if (wid.startsWith("P-")) {
          const pw = widgetMapRef.current.get(wid);
          if (pw && pw.items) {
            const nItems = Object.fromEntries(
              Object.entries(pw.items).map(([key, item]) => {
                const updatedItem = widget.items[key];
                return [key, updatedItem || item];
              })
            );
            nw = { ...pw, items: nItems };
          }
        }
        widgetMapRef.current.set(wid, nw);

        for (const cardId of widget2CardRef.current.get(wid)) {
          const card = cardMapRef.current.get(cardId);
          if (card) {
            const newWidgets = card.widgets.map((w) =>
              w.id === wid ? { ...w, ...nw, prepared: false } : w
            );
            const newCard = { ...card, widgets: newWidgets };
            cardMapRef.current.set(newCard.id, newCard);
            hasUpdates = true;
          }
        }
      }

      if (hasUpdates) {
        setCardList(Array.from(cardMapRef.current.values()));
        setWidgetNames(
          Array.from(widgetMapRef.current.values()).map((w) => w.name)
        );
      }
    };

    const handleCardUpdateEvent = (card) => {
      if (!isInitialized.current) {
        console.log("Dashboard not initialized yet, ignoring card update");
        return;
      }

      const existingCard = cardMapRef.current.get(card.cardId);
      if (!existingCard) {
        return;
      }

      const updatedCard = {
        ...existingCard,
        setting: card.setting || existingCard.setting,
        widgets: existingCard.widgets.map((widget) => {
          const updatedWidget = card.widgets.find((w) => w.id === widget.id);
          if (updatedWidget) {
            return {
              ...widget,
              ...updatedWidget,
              prepared: widget.prepared,
            };
          }
          return widget;
        }),
      };

      cardMapRef.current.set(card.cardId, updatedCard);

      updatedCard.widgets.forEach((widget) => {
        const widgetId = widget.id;

        let cardSet = widget2CardRef.current.get(widgetId);
        if (!cardSet || !(cardSet instanceof Set)) {
          cardSet = Array.isArray(cardSet) ? new Set(cardSet) : new Set();
        }
        cardSet.add(card.cardId);

        if (!widgetMapRef.current.has(widgetId)) {
          widgetMapRef.current.set(widgetId, widget);
        } else {
          const existingWidget = widgetMapRef.current.get(widgetId);
          widgetMapRef.current.set(widgetId, {
            ...existingWidget,
            ...widget,
          });
        }
      });

      setCardList(Array.from(cardMapRef.current.values()));
      setWidgetNames(
        Array.from(widgetMapRef.current.values()).map((w) => w.name)
      );

      if (card.layout && !isEqual(existingCard.layout, card.layout)) {
        const newLayouts = generateLayouts(
          [...cardList],
          getCols(),
          getCurrentLayoutSize()
        );
        setLayouts(newLayouts);
      }
    };

    const handleAutomationRunningEvent = (payload) => {
      const { automationId, running } = payload;

      for (const [widgetId, widget] of widgetMapRef.current.entries()) {
        if (widget.automationId === automationId) {
          const updatedWidget = {
            ...widget,
            running,
          };
          widgetMapRef.current.set(widgetId, updatedWidget);

          const cardIds = widget2CardRef.current.get(widgetId);
          if (cardIds) {
            cardIds.forEach((cardId) => {
              const card = cardMapRef.current.get(cardId);

              if (card) {
                const newWidgets = card.widgets.map((w) => {
                  if (w.id === widgetId) {
                    return {
                      ...w,
                      running,
                    };
                  }
                  return w;
                });

                const newCard = { ...card, widgets: newWidgets };
                cardMapRef.current.set(cardId, newCard);
              }
            });
          }
          setCardList(Array.from(cardMapRef.current.values()));
        }
      }
    };

    const moveCardToCanvas = async (card, targetCanvasId, targetCanvasName) => {
      try {
        isMovingCardRef.current = true;
        skipEvent.current = true;
        openBackdrop("Moving card...");

        const copyResponse = await copyCardToCanvas(
          apiCall,
          userInfo.activeScope.id,
          targetCanvasId,
          card.id,
          curCanvasRef.current
        );

        if (!copyResponse.success) {
          throw new Error("Failed to copy card to target canvas");
        }

        successToast(`Card moved to ${targetCanvasName}`);
        dragClose();

        history.replace(`/dashboard?canvas=${targetCanvasId}`);
        await loadDashboard(targetCanvasId);
      } catch (error) {
        console.error("Error moving card between canvases:", error);
        errorToast("Failed to move card");
      } finally {
        closeBackdrop();
        setDraggedCard(null);
        skipEvent.current = false;
      }
    };

    const getCurrentLayoutSize = () => {
      const width = window.innerWidth;
      if (width <= 768) return "sm";
      if (width <= 1200) return "md";
      return "lg";
    };

    const prepareCards = (cardList, widgetList, automations) => {
      const layoutSize = getCurrentLayoutSize();

      widgetMapRef.current.clear();
      cardMapRef.current.clear();

      const processedWidgets = widgetList.map((widget) => {
        if (widget.source === "automation") {
          const matchedAutomation = automations.find(
            (auto) => auto.automationId === widget.automationId
          );

          if (matchedAutomation) {
            return {
              ...widget,
              enabled: matchedAutomation.enabled,
              running: matchedAutomation.running,
            };
          }
        }
        return widget;
      });

      for (let i = 0; i < processedWidgets.length; i++) {
        widgetMapRef.current.set(processedWidgets[i].id, processedWidgets[i]);
      }
      setWidgetNames(
        Array.from(widgetMapRef.current.values()).map((w) => w.name)
      );

      const cards = cardList
        .map((cd) => {
          // Store all layout sizes
          const layouts = {
            sm: cd.layouts?.sm || { w: 2, h: 2, x: 0, y: 0 },
            md: cd.layouts?.md || { w: 2, h: 2, x: 0, y: 0 },
            lg: cd.layouts?.lg || { w: 2, h: 2, x: 0, y: 0 },
          };

          const detailedWidgets = cd.widgets
            .filter((widget) => widgetMapRef.current.has(widget.id))
            .map((widget) => {
              const fw = widgetMapRef.current.get(widget.id);
              const updated = { ...widget, ...fw };
              return updated;
            })
            .filter((widget) => widget !== undefined && widget !== null);

          detailedWidgets.forEach((widget) => {
            if (!widget2CardRef.current.has(widget.id)) {
              widget2CardRef.current.set(widget.id, [cd.cardId]);
            } else {
              const currentArray = widget2CardRef.current.get(widget.id);
              widget2CardRef.current.set(widget.id, [
                ...currentArray,
                cd.cardId,
              ]);
            }
          });

          return detailedWidgets.length > 0
            ? {
                id: cd.cardId,
                layouts: layouts,
                setting: cd.setting,
                widgets: detailedWidgets,
              }
            : null;
        })
        .filter((group) => group !== null);

      for (let i = 0; i < cards.length; i++) {
        cardMapRef.current.set(cards[i].id, cards[i]);
      }

      setCardList(cards);
      const newLayouts = generateLayouts(cards, getCols(), layoutSize);
      setLayouts(newLayouts);
      isInitialized.current = true;
    };

    const loadDashboard = async (newCanvasId = null) => {
      try {
        setContentLoading(true);
        setShowContent(false);

        let targetCanvasId = newCanvasId;

        if (newCanvasId && newCanvasId === curCanvasRef.current) {
          console.log("newCanvasId1", newCanvasId);
          return;
        }
        if (newCanvasId) {
          // Only replace history path if current path is /dashboard
          if (history.location.pathname === "/dashboard") {
            history.replace(`/dashboard?canvas=${newCanvasId}`);
          }
          curCanvasRef.current = newCanvasId;
        }
        if (!targetCanvasId) {
          const searchParams = new URLSearchParams(window.location.search);
          const urlCanvasId = searchParams.get("canvas");
          targetCanvasId = urlCanvasId || targetCanvasId;
        }

        setLayouts(null);
        setCardList([]);
        setWidgetNames([]);
        isInitialized.current = false;
        skipEvent.current = true;

        widget2CardRef.current.clear();
        cardMapRef.current.clear();
        widgetMapRef.current.clear();
        revisionRef.current = -1;

        const payload = targetCanvasId ? { canvasId: targetCanvasId } : {};
        const response = await sendAndWaitWs(
          "/ui/canvas/get",
          JSON.stringify(payload)
        );

        if (response.error) {
          throw new Error(response.error);
        }

        const jsonData = JSON.parse(response.payload);

        const currentCanvasId = jsonData.canvas.id;

        if (!newCanvasId) {
          // Only replace history path if current path is /dashboard
          if (history.location.pathname === "/dashboard") {
            history.replace(`/dashboard?canvas=${currentCanvasId}`);
          }
          curCanvasRef.current = currentCanvasId;
        }

        revisionRef.current = jsonData.canvas.revision;
        prepareCards(
          jsonData.canvas.cards,
          jsonData.widgets,
          jsonData.automations
        );

        closeBackdrop();
        skipEvent.current = false;

        setContentLoading(false);
        setShowContent(true);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        isInitialized.current = false;
        errorToast("Failed to load dashboard");

        if (newCanvasId) {
          history.replace(`/dashboard?canvas=${curCanvasRef.current}`);
        }

        setContentLoading(false);
      }
    };

    useEffect(() => {
      const loadCanvasList = async () => {
        if (isCanvasListLoaded || canvasListLoadingRef.current) {
          return;
        }

        try {
          canvasListLoadingRef.current = true;
          const resp = await listCanvas(apiCall, userInfo.activeScope.id);
          if (resp.canvas) {
            setCanvasList(resp.canvas);
            setIsCanvasListLoaded(true);
          } else {
            errorToast("Get Canvas Error");
          }
        } catch (error) {
          console.error("Error loading canvas list:", error);
          errorToast("Failed to load canvas list");
        } finally {
          canvasListLoadingRef.current = false;
        }
      };

      loadCanvasList();
    }, [isCanvasListLoaded, apiCall, userInfo.activeScope?.id]);

    useEffect(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        closeBackdrop();
        loadDashboard();
      }
    }, [ws, ws?.readyState]);

    const handleOpenOptionDialog = () => {
      setOpenOptionDialog(true);
    };

    const handleNewWidget = () => {
      setCurrentWidgets([]);
      setOpenWidgetDialog(true);
      setOpenOptionDialog(false);
    };

    const handleNewDeviceTwin = () => {
      setCurrentWidgets([]);
      setOpenDeviceDialog(true);
      setOpenOptionDialog(false);
    };

    // useImperativeHandle(ref, () => ({
    //   handleNewWidgetConfig
    // }));

    const handleCloseWidgetDialog = () => {
      setOpenWidgetDialog(false);
    };

    const handleCloseDeviceDialog = () => {
      setOpenDeviceDialog(false);
    };

    const handleCloseOptionDialog = () => {
      setOpenOptionDialog(false);
    };

    const handleCloseCardDialog = () => {
      setOpenCardDialog(false);
    };

    const handleCloseDeleteDialog = () => {
      setOpenDeleteDialog(false);
    };

    const handleUpsertWidgets = async (widgetItems) => {
      if (widgetItems.length <= 0) {
        return;
      }
      const isNew =
        widgetItems[0].id === null || widgetItems[0].id === undefined;
      setIsUpsertingWidget(true);
      skipEvent.current = true;
      try {
        const success = await upsertWidget(
          apiCall,
          userInfo.activeScope.id,
          curCanvasRef.current,
          widgetItems
        );
        if (success) {
          successToast("Saved");
          await loadDashboard();
        } else {
          errorToast("Error! Check the name and config.");
        }
      } catch (error) {
        errorToast("Error! Check the name and config.");
      } finally {
        setIsUpsertingWidget(false);
        skipEvent.current = false;
      }
    };

    const handleUpsertDeviceTwin = () => {
      loadDashboard();
    };

    const handleBeforeUpsertDeviceTwin = () => {
      skipEvent.current = true;
    };

    const handleUpdateCard = async (card) => {
      const newCard = {
        id: card.id,
        name: card.name,
        widgetIds: card.widgets.map((w) => w.id),
      };
      skipEvent.current = true;
      const success = await updateCanvasCards(
        apiCall,
        userInfo.activeScope.id,
        curCanvasRef.current,
        [newCard]
      );
      if (success) {
        successToast("Saved");
        loadDashboard();
        setOpenCardDialog(false);
        setEdit(null);
      } else {
        errorToast("Error!");
      }
    };

    const handleDeleteCard = async (cardId) => {
      revisionRef.current = -1;
      const success = await deleteCard(
        apiCall,
        userInfo.activeScope.id,
        curCanvasRef.current,
        cardId
      );
      if (success) {
        successToast("Deleted");
        setOpenCardDialog(false);
        await loadDashboard();
        setEdit(null);
      } else {
        errorToast("Remove card error!");
      }
    };

    const handleUpdateCardLayout = async () => {
      const cl = getCurrentLayoutSize();
      const layouts = cardList.map((c) => {
        return { id: c.id, layout: c.layout, layoutSize: cl };
      });

      // Check if any layout has actually changed
      const hasLayoutChanged = cardList.some((card) => {
        const currentLayout = card.layouts[cl];
        return (
          currentLayout.x !== card.layout.x ||
          currentLayout.y !== card.layout.y ||
          currentLayout.w !== card.layout.w ||
          currentLayout.h !== card.layout.h
        );
      });

      if (!hasLayoutChanged) {
        return; // Skip API call if no changes
      }

      skipEvent.current = true;
      const success = await updateCardLayout(
        apiCall,
        userInfo.activeScope.id,
        curCanvasRef.current,
        layouts
      );
      if (success) {
        successToast("Saved");
        await loadDashboard();
      } else {
        errorToast("Error!");
      }
    };

    const handleUpdateWidgetLayout = async (widgets) => {
      if (editCard) {
        const resp = await updateCardContent(
          apiCall,
          userInfo.activeScope.id,
          editCard.id,
          widgets,
          null
        );
        if (resp) {
          successToast("Saved");
          handleWidgetLayoutChange(widgets);
          setEdit(null);
        } else {
          errorToast("Error!");
        }
      } else {
        errorToast("Error!");
      }
    };

    const handleEditWidgets = async (widgetIds) => {
      console.log(widgetIds);
      const widgets = widgetIds.map((wid) => widgetMapRef.current.get(wid));
      setCurrentWidgets(widgets);
      setOpenWidgetDialog(true);
    };

    const handleOpenDeleteCard = async (card) => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      mouseDownRef.current = false;
      setIsLongPress(false);
      setPressingCardId(null);
      setEdit(null);
      setCurrentCard(card);
      setOpenDeleteDialog(true);
    };

    const handleOpenTwinAutomation = (card) => {
      history.push(`/automation/twin/list?cardId=${card.id}`);
    };

    const handleOpenAutomation = (card) => {
      console.log(card);
      const aid = card.widgets
        .filter((widget) => widget.automationId)
        .map((widget) => widget.automationId)[0];
      history.push(`/automation/overview?id=${aid}`);
    };

    const getCardSource = (card) => {
      if (card.widgets.length > 0) {
        return card.widgets[0].source;
      }
      return "";
    };

    const handleCopyCard = async (card) => {
      revisionRef.current = -1;
      const resp = await copyCard(
        apiCall,
        userInfo.activeScope.id,
        curCanvasRef.current,
        card.id
      );
      if (resp.success) {
        successToast("Copy Successful");
        await loadDashboard();
        setEdit(null);
      } else {
        errorToast("Copy card error!");
      }
    };

    const debouncedUpdate = useCallback(
      debounce(async (widgetItems) => {
        try {
          const resp = await updateWidgetData(sendAndWaitWs, widgetItems);
          if (!resp) {
            errorToast("Widget Update Error!");
          }
        } catch (error) {
          console.error("Failed to update widget:", error);
          errorToast("Error!");
        }
      }, 100),
      [sendAndWaitWs]
    );

    useEffect(() => {
      return () => {
        debouncedUpdate.cancel();
      };
    }, [debouncedUpdate]);

    const handleUiWidgetValueChange = async (widgetItem) => {
      const widgetItems = Array.isArray(widgetItem) ? widgetItem : [widgetItem];

      widgetItems.forEach((item) => {
        latestWidgetValues.current[item.id] = item;
      });

      debouncedUpdate(widgetItems);
    };

    const handleUpdateEditCard = async (card) => {
      // if (card?.widget.length > 0) {
      //   const widgetItems = Array.isArray(card.widget)
      //     ? card.widget
      //     : [card.widget];

      //   widgetItems.forEach((item) => {
      //     latestWidgetValues.current[item.id] = item;
      //   });
      //   console.log(widgetItems);
      //   handleUiWidgetValueChange(widgetItems);
      // }
      if (card?.setting) {
        const result = await updateCardContent(
          apiCall,
          userInfo.activeScope.id,
          currentEditCard.id,
          null,
          card.setting
        );
        if (result) {
          successToast("Set Color Success");
        } else {
          errorToast("Color Error");
        }
      }
    };

    useEffect(() => {
      const handleGlobalMouseUp = (e) => {
        if (mouseDownRef.current) {
          mouseDownRef.current = false;
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
          }
          setIsLongPress(false);
          if (!isLongPress) {
            setEdit(null);
          }
          setPressingCardId(null);
        }
      };

      window.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        window.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }, [isLongPress]);

    const handleLongPressStart = (card, id, e) => {
      if (!e) return;
      if (drag) return;
      if (e.type === "mousedown" && e.button !== 0) return;
      e.preventDefault();

      // Store initial touch/mouse position and time
      touchStartPositionRef.current = {
        x: e.type === "touchstart" ? e.touches[0].clientX : e.clientX,
        y: e.type === "touchstart" ? e.touches[0].clientY : e.clientY,
      };

      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      mouseDownRef.current = true;
      setPressingCardId(id);
      setIsLongPress(false);

      longPressTimerRef.current = setTimeout(() => {
        if (mouseDownRef.current) {
          setIsLongPress(true);
          setEdit(id);
          setCurrentEditCard(card);
          setPressingCardId(null);
        }
      }, 600);
    };

    const handleLongPressCancel = (e) => {
      if (mouseDownRef.current) {
        // Check if there was significant movement
        if (touchStartPositionRef.current) {
          const currentPosition = {
            x: e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX,
            y: e.type === "touchend" ? e.changedTouches[0].clientY : e.clientY,
          };

          const movement = Math.sqrt(
            Math.pow(currentPosition.x - touchStartPositionRef.current.x, 2) +
              Math.pow(currentPosition.y - touchStartPositionRef.current.y, 2)
          );

          // If there was significant movement or not enough time passed, cancel the long press
          if (movement > movementThreshold) {
            if (longPressTimerRef.current) {
              clearTimeout(longPressTimerRef.current);
              longPressTimerRef.current = null;
            }
            setIsLongPress(false);
            setEdit(null);
          }
        }

        mouseDownRef.current = false;
        touchStartPositionRef.current = null;
        setPressingCardId(null);
      }
    };

    // Add touchmove handler to cancel long press during scrolling
    const handleTouchMove = (e) => {
      if (mouseDownRef.current && touchStartPositionRef.current) {
        const currentPosition = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };

        const movement = Math.sqrt(
          Math.pow(currentPosition.x - touchStartPositionRef.current.x, 2) +
            Math.pow(currentPosition.y - touchStartPositionRef.current.y, 2)
        );

        if (movement > movementThreshold) {
          handleLongPressCancel(e);
        }
      }
    };

    const handleCardLayoutChange = (currentLayout) => {
      if (edit === null) {
        const updatedCardList = cardList.map((card) => {
          const layoutItem = currentLayout.find(
            (layout) => layout.i === card.id
          );
          if (layoutItem) {
            card.layout = {
              ...card.layout,
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            };
            return card;
          }
          return card;
        });

        setCardList(updatedCardList);
        setLayouts(currentLayout);
        setLayoutChanged(true);
      } else {
        const updatedLayout = currentLayout.map((layout) => {
          if (layout.i === edit) {
            return {
              ...layout,
              h: layout.h,
            };
          }
          return layout;
        });
        setLayouts(updatedLayout);

        const updatedCardList = cardList.map((card) => {
          const layoutItem = updatedLayout.find(
            (layout) => layout.i === card.id
          );
          if (layoutItem) {
            card.layout = {
              ...card.layout,
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            };
            return card;
          }
          return card;
        });
        setCardList(updatedCardList);
        setLayoutChanged(true);
      }
    };

    const handleWidgetLayoutChange = (currentLayout) => {
      if (editCard) {
        const updatedCardList = cardList.map((card) => {
          if (card.id === editCard.id) {
            const updatedWidgets = card.widgets.map((widget) => {
              const layoutItem = currentLayout.find(
                (layout) => layout.id === widget.id
              );
              console.log("layoutItem", layoutItem);
              if (layoutItem) {
                return {
                  ...widget,
                  layouts: {
                    ...widget.layouts,
                    [layoutItem.layoutSize]: {
                      ...widget.layouts[layoutItem.layoutSize],
                      x: Number(layoutItem.layout.x),
                      y: Number(layoutItem.layout.y),
                      w: Number(layoutItem.layout.w),
                      h: Number(layoutItem.layout.h),
                    },
                  },
                };
              }
              return widget;
            });

            return {
              ...card,
              widgets: updatedWidgets,
            };
          }
          return card;
        });

        setCardList(updatedCardList);
        setLayoutChanged(true);
      }
    };

    const getCols = () => {
      const width = window.innerWidth;
      if (width <= 768) return 6;
      if (width <= 1200) return 10;
      return 16;
    };

    const getCardWidth = (cardId) => {
      const layout = layouts.find((item) => item.i === cardId);
      return layout ? layout.w : 0;
    };

    const handleMultiSelect = (cardId) => {
      setIsMultiSelectMode(true);
      setEdit(null);
      setAnchorEl(null);
      if (cardId) {
        setSelectedCards([cardId]);
      } else {
        setSelectedCards([]);
      }
    };

    const handleBatchDelete = async () => {
      setIsBatchDeleteDialogOpen(true);
    };

    const handleBatchDeleteConfirm = async () => {
      setIsBatchDeleting(true);
      try {
        await batchDeleteCard(
          apiCall,
          userInfo.activeScope.id,
          curCanvasRef.current,
          selectedCards
        );
        setSelectedCards([]);
        successToast("Cards deleted successfully");
        await loadDashboard();
      } catch (error) {
        errorToast("Failed to delete cards");
      } finally {
        setIsBatchDeleting(false);
        setIsBatchDeleteDialogOpen(false);
      }
    };

    const handleCardSelect = (cardId) => {
      setSelectedCards((prev) => {
        if (prev.includes(cardId)) {
          return prev.filter((id) => id !== cardId);
        } else {
          return [...prev, cardId];
        }
      });
    };

    useEffect(() => {
      // Register data widgets once
      registerDataWidgets();
    }, []);

    useEffect(() => {
      const resizeObserverError = (error) => {
        if (error.message.includes("ResizeObserver")) {
          error.stopImmediatePropagation();
        }
      };

      window.addEventListener("error", resizeObserverError);
      return () => window.removeEventListener("error", resizeObserverError);
    }, []);

    const debouncedHandleResize = useCallback(
      debounce(() => {
        if (cardList.length > 0) {
          console.log("cardList", getCurrentLayoutSize());
          setLayouts(
            generateLayouts(cardList, getCols(), getCurrentLayoutSize())
          );
          loadDashboard();
        }
      }, 500),
      [cardList]
    );

    const lastLayoutSize = useRef(getCurrentLayoutSize());

    useEffect(() => {
      const checkLayoutState = () => {
        const currentLayoutSize = getCurrentLayoutSize();

        if (lastLayoutSize.current !== currentLayoutSize) {
          lastLayoutSize.current = currentLayoutSize;
          debouncedHandleResize();
        }

        rafId = requestAnimationFrame(checkLayoutState);
      };

      let rafId = requestAnimationFrame(checkLayoutState);

      // Also attach to any events that might help
      const handleAnyEvent = () => {
        setTimeout(checkLayoutState, 0);
      };

      window.addEventListener("resize", handleAnyEvent);
      window.addEventListener("focus", handleAnyEvent);
      window.addEventListener("blur", handleAnyEvent);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", handleAnyEvent);
        window.removeEventListener("focus", handleAnyEvent);
        window.removeEventListener("blur", handleAnyEvent);
        debouncedHandleResize.cancel();
      };
    }, [debouncedHandleResize]);

    useEffect(() => {
      if (
        isMobile &&
        tabsContainerRef.current &&
        curCanvasRef.current &&
        !drag
      ) {
        const activeTab = tabsContainerRef.current.querySelector(
          `[data-canvas-id="${curCanvasRef.current}"]`
        );
        if (activeTab) {
          const containerWidth = tabsContainerRef.current.offsetWidth;
          const tabLeft = activeTab.offsetLeft;
          const tabWidth = activeTab.offsetWidth;
          const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

          tabsContainerRef.current.scrollTo({
            left: scrollLeft,
          });
        }
      }
    }, [isMobile, canvasList, curCanvasRef.current, drag]);

    const handleDragStart = (
      layout,
      oldItem,
      newItem,
      placeholder,
      e,
      element
    ) => {
      if (!drag) return;
      const cardId = newItem.i;
      const card = cardList.find((c) => c.id === cardId);

      if (card) {
        setDraggedCard({
          card,
          sourceCanvasId: curCanvasRef.current,
        });
        element.style.opacity = "0.7";
        element.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
        element.style.zIndex = "10";
      }
    };

    const handleDragStop = (
      layout,
      oldItem,
      newItem,
      placeholder,
      e,
      element
    ) => {
      if (!drag) return;
      element.style.opacity = "";
      element.style.boxShadow = "";
      element.style.zIndex = "";

      // Clear auto-scroll interval when drag stops
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };

    const handleDrag = (layout, oldItem, newItem, placeholder, e, element) => {
      if (!drag || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      // Get coordinates from either mouse or touch event
      const mouseY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      const scrollThreshold = 100; // pixels from top/bottom to trigger scroll
      const scrollSpeed = 5; // pixels per scroll

      // Clear existing interval
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }

      // Calculate the mouse position relative to the viewport
      const relativeY = mouseY - containerRect.top;
      const containerHeight = containerRect.height;

      // Check if mouse/touch is near top or bottom of container
      if (relativeY < scrollThreshold) {
        // Scroll up
        autoScrollIntervalRef.current = setInterval(() => {
          const newScrollTop = Math.max(0, container.scrollTop - scrollSpeed);
          container.scrollTop = newScrollTop;
        }, 16);
      } else if (containerHeight - relativeY < scrollThreshold) {
        // Scroll down
        autoScrollIntervalRef.current = setInterval(() => {
          const maxScroll = container.scrollHeight - container.clientHeight;
          const newScrollTop = Math.min(
            maxScroll,
            container.scrollTop + scrollSpeed
          );
          container.scrollTop = newScrollTop;
        }, 16);
      }
    };

    // Add touch event handlers
    useEffect(() => {
      if (!drag || !containerRef.current) return;

      const container = containerRef.current;

      const handleTouchMove = (e) => {
        if (!drag) return;
        const touch = e.touches[0];
        const containerRect = container.getBoundingClientRect();
        const touchY = touch.clientY;
        const scrollThreshold = 100;
        const scrollSpeed = 5;

        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }

        const relativeY = touchY - containerRect.top;
        const containerHeight = containerRect.height;

        if (relativeY < scrollThreshold) {
          autoScrollIntervalRef.current = setInterval(() => {
            const newScrollTop = Math.max(0, container.scrollTop - scrollSpeed);
            container.scrollTop = newScrollTop;
          }, 16);
        } else if (containerHeight - relativeY < scrollThreshold) {
          autoScrollIntervalRef.current = setInterval(() => {
            const maxScroll = container.scrollHeight - container.clientHeight;
            const newScrollTop = Math.min(
              maxScroll,
              container.scrollTop + scrollSpeed
            );
            container.scrollTop = newScrollTop;
          }, 16);
        }
      };

      const handleTouchEnd = () => {
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
      };

      container.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      container.addEventListener("touchend", handleTouchEnd);
      container.addEventListener("touchcancel", handleTouchEnd);

      return () => {
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
        container.removeEventListener("touchcancel", handleTouchEnd);
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
      };
    }, [drag]);

    return (
      <>
        <GlobalStyle />
        {hoveredTab && (
          <Box
            sx={{
              position: "fixed",
              left: hoveredTab.x,
              top: hoveredTab.y,
              transform: "translateX(-50%)",
              backgroundColor: "background.paper",
              color: "text.primary",
              padding: "6px 12px",
              borderRadius: 1,
              boxShadow: theme.shadows[2],
              fontSize: "0.875rem",
              fontWeight: 500,
              zIndex: 1300,
              pointerEvents: "none",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -6,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderBottom: `6px solid ${theme.palette.background.paper}`,
              },
            }}
          >
            Move to {hoveredTab.name}
          </Box>
        )}
        <Box
          sx={{
            height: "100%",
            visibility: "visible",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "40px",
                      background:
                        "linear-gradient(to right, transparent, background.paper)",
                      pointerEvents: "none",
                      zIndex: 1,
                    },
                  }}
                >
                  <TableContainer
                    ref={tabsContainerRef}
                    sx={{
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        height: "6px",
                        display: "none",
                      },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      scrollBehavior: "smooth",
                      "&:hover::-webkit-scrollbar": {
                        display: "block",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "divider",
                        borderRadius: "3px",
                        "&:hover": {
                          background: "text.secondary",
                        },
                      },
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        width: "auto",
                        "& .MuiTableCell-root": {
                          borderBottom: "none",
                          py: 1.5,
                          px: 2,
                          whiteSpace: "nowrap",
                          minHeight: "32px",
                        },
                      }}
                    >
                      <TableBody>
                        <TableRow
                          sx={{
                            display: "flex",
                            flexWrap: drag
                              ? "wrap"
                              : { xs: "nowrap", sm: "wrap" },
                            gap: { xs: 0 },
                            margin: { xs: 0 },
                            py: 1,
                            px: 1,
                          }}
                        >
                          {canvasList.map((canvas) => (
                            <TableCell
                              key={canvas.id}
                              className="canvas-tab"
                              data-canvas-id={canvas.id}
                              data-canvas-name={canvas.name}
                              padding="none"
                              sx={{
                                cursor: "pointer",
                                textAlign: "left",
                                position: "relative",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                letterSpacing: "0.01em",
                                userSelect: "none",
                                width: { xs: "fit-content", sm: "auto" },
                                flex: { xs: "0 0 auto" },
                                display: "flex",
                                alignItems: "center",
                                opacity: 1,
                                margin: { xs: "2px 6px", sm: "4px 8px" },
                                padding: "0 12px !important",
                                height: "28px !important",
                                backgroundColor:
                                  canvas.id === curCanvasRef.current
                                    ? "primary.main"
                                    : "background.card",
                                color:
                                  canvas.id === curCanvasRef.current
                                    ? "primary.contrastText"
                                    : "text.secondary",
                                borderRadius: 2,
                                boxShadow: "none",
                                "&.drag-hover": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.15
                                  ),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                  transform: "translateY(-2px)",
                                  boxShadow: `0 2px 8px ${alpha(
                                    theme.palette.primary.main,
                                    0.25
                                  )}`,
                                  "& > span": {
                                    color: theme.palette.primary.main,
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      0.08
                                    ),
                                    borderRadius: 2,
                                    zIndex: -1,
                                  },
                                },
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  backgroundColor:
                                    canvas.id === curCanvasRef.current
                                      ? "primary.dark"
                                      : alpha(theme.palette.primary.main, 0.08),
                                },
                                WebkitTapHighlightColor: "transparent",
                                touchAction: "manip ulation",
                              }}
                              onClick={() => {
                                if (!draggedCard) {
                                  loadDashboard(canvas.id);
                                }
                              }}
                            >
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadDashboard(canvas.id);
                                }}
                              >
                                {canvas.name}
                              </span>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            ref={containerRef}
            sx={{
              flex: 1,
              overflow: "auto",
              position: "relative",
              height: "calc(100% - 48px)",
              paddingBottom: 2,
              opacity: showContent ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
            }}
          >
            {contentLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <LoadingSpinner />
              </Box>
            ) : (
              <>
                <style>
                  {`
                    .react-grid-item {
                      transition: none !important;
                      animation: none !important;
                    }
                    .react-grid-layout {
                      transition: none !important;
                      animation: none !important;
                    }
                  `}
                </style>

                {cardList.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      color: "text.secondary",
                      fontSize: "1.2rem",
                      fontWeight: 500,
                    }}
                  >
                    Empty canvas
                  </Box>
                ) : (
                  <ResponsiveGridLayout
                    className="layout"
                    layouts={{
                      lg: Array.isArray(layouts) ? layouts : [],
                      md: Array.isArray(layouts) ? layouts : [],
                      sm: Array.isArray(layouts) ? layouts : [],
                      xs: Array.isArray(layouts) ? layouts : [],
                      xxs: Array.isArray(layouts) ? layouts : [],
                    }}
                    breakpoints={{
                      lg: 1200,
                      md: 996,
                      sm: 768,
                      xs: 480,
                      xxs: 0,
                    }}
                    cols={{ lg: 16, md: 10, sm: 10, xs: 6, xxs: 6 }}
                    rowHeight={8}
                    onLayoutChange={handleCardLayoutChange}
                    compactType={null}
                    preventCollision={true}
                    isDraggable={drag}
                    isResizable={drag}
                    margin={[8, 8]}
                    useCSSTransforms={false}
                    draggableCancel=".non-draggable"
                    transitionDuration={0}
                    animate={false}
                    autoSize={false}
                    onDragStart={handleDragStart}
                    onDragStop={handleDragStop}
                    onDrag={handleDrag}
                    style={{
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      MozUserSelect: "none",
                      msUserSelect: "none",
                      WebkitTapHighlightColor: "transparent",
                      touchAction: "manipulation",
                    }}
                  >
                    {cardList.map((card) => (
                      <div
                        key={card.id}
                        style={{
                          position: "relative",
                          height: "100%",
                          cursor: drag ? "move" : "pointer",
                          opacity: 1,
                          transform: "none",
                          willChange: "auto",
                        }}
                      >
                        {edit === card.id && (
                          <Box
                            sx={{
                              height: "40px",
                              backgroundColor: "background.card",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0 8px",
                              borderRadius: "16px 16px 0 0",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                              zIndex: 2,
                            }}
                          >
                            {getCardWidth(card.id) < 4 ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <IconButton
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setAnchorEl(e.currentTarget);
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                                <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl)}
                                  onClose={() => setAnchorEl(null)}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      isEditTitle !== card.id
                                        ? setIsEditTitle(card.id)
                                        : setIsEditTitle(null);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      {isEditTitle === card.id ? (
                                        <EditOffIcon />
                                      ) : (
                                        <EditIcon />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText>
                                      {isEditTitle === card.id
                                        ? "EditOff"
                                        : "Configure"}
                                    </ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setEditCard(card);
                                      setEditCardLayout(true);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <GridOnIcon />
                                    </ListItemIcon>
                                    <ListItemText>Layout</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleMultiSelect(card.id);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CheckBoxIcon />
                                    </ListItemIcon>
                                    <ListItemText>Multi-select</ListItemText>
                                  </MenuItem>
                                  {getCardSource(card) === "twin" && (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenTwinAutomation(card);
                                        setAnchorEl(null);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AutoModeIcon />
                                      </ListItemIcon>
                                      <ListItemText>Automation</ListItemText>
                                    </MenuItem>
                                  )}
                                  {getCardSource(card) === "automation" && (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenAutomation(card);
                                        setAnchorEl(null);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AutoModeIcon />
                                      </ListItemIcon>
                                      <ListItemText>Automation</ListItemText>
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleCopyCard(card);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <ContentCopy />
                                    </ListItemIcon>
                                    <ListItemText>Copy</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleOpenDeleteCard(card);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <DeleteIcon />
                                    </ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      if (isEditTitle) {
                                        return;
                                      }
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (longPressTimerRef.current) {
                                        clearTimeout(longPressTimerRef.current);
                                      }
                                      setPressingCardId(null);
                                      setEdit(null);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CloseIcon />
                                    </ListItemIcon>
                                    <ListItemText>Close</ListItemText>
                                  </MenuItem>
                                </Menu>
                              </Box>
                            ) : getCardWidth(card.id) < 5 ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <IconButton
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setAnchorEl(e.currentTarget);
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                                <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl)}
                                  onClose={() => setAnchorEl(null)}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      isEditTitle !== card.id
                                        ? setIsEditTitle(card.id)
                                        : setIsEditTitle(null);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      {isEditTitle === card.id ? (
                                        <EditOffIcon />
                                      ) : (
                                        <EditIcon />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText>
                                      {isEditTitle === card.id
                                        ? "EditOff"
                                        : "Configure"}
                                    </ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setEditCard(card);
                                      setEditCardLayout(true);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <GridOnIcon />
                                    </ListItemIcon>
                                    <ListItemText>Layout</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleMultiSelect(card.id);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CheckBoxIcon />
                                    </ListItemIcon>
                                    <ListItemText>Multi-select</ListItemText>
                                  </MenuItem>
                                  {getCardSource(card) === "twin" && (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenTwinAutomation(card);
                                        setAnchorEl(null);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AutoModeIcon />
                                      </ListItemIcon>
                                      <ListItemText>Automation</ListItemText>
                                    </MenuItem>
                                  )}
                                  {getCardSource(card) === "automation" && (
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenAutomation(card);
                                        setAnchorEl(null);
                                      }}
                                    >
                                      <ListItemIcon>
                                        <AutoModeIcon />
                                      </ListItemIcon>
                                      <ListItemText>Automation</ListItemText>
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleCopyCard(card);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <ContentCopy />
                                    </ListItemIcon>
                                    <ListItemText>Copy</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleOpenDeleteCard(card);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <DeleteIcon />
                                    </ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={(e) => {
                                      if (isEditTitle) {
                                        return;
                                      }
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (longPressTimerRef.current) {
                                        clearTimeout(longPressTimerRef.current);
                                      }
                                      setPressingCardId(null);
                                      setEdit(null);
                                      setAnchorEl(null);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CloseIcon />
                                    </ListItemIcon>
                                    <ListItemText>Close</ListItemText>
                                  </MenuItem>
                                </Menu>
                                <IconButton
                                  aria-label="close"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    if (isEditTitle) {
                                      return;
                                    }
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (longPressTimerRef.current) {
                                      clearTimeout(longPressTimerRef.current);
                                    }
                                    setPressingCardId(null);
                                    setEdit(null);
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  {isEditTitle !== card.id && (
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setIsEditTitle(card.id);
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                  {isEditTitle === card.id && (
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setIsEditTitle(null);
                                      }}
                                    >
                                      <EditOffIcon />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setEditCard(card);
                                      setEditCardLayout(true);
                                    }}
                                  >
                                    <GridOnIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="multi-select"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleMultiSelect(card.id);
                                    }}
                                  >
                                    <CheckBoxIcon />
                                  </IconButton>
                                  {getCardSource(card) === "twin" && (
                                    <IconButton
                                      aria-label="automation"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenTwinAutomation(card);
                                      }}
                                    >
                                      <AutoModeIcon />
                                    </IconButton>
                                  )}
                                  {getCardSource(card) === "automation" && (
                                    <IconButton
                                      aria-label="automation"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenAutomation(card);
                                      }}
                                    >
                                      <AutoModeIcon />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    aria-label="copy"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleCopyCard(card);
                                    }}
                                  >
                                    <ContentCopy />
                                  </IconButton>
                                  <IconButton
                                    aria-label="delete"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleOpenDeleteCard(card);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                <IconButton
                                  aria-label="close"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    if (isEditTitle) {
                                      return;
                                    }
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (longPressTimerRef.current) {
                                      clearTimeout(longPressTimerRef.current);
                                    }
                                    setPressingCardId(null);
                                    setEdit(null);
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        )}

                        {isMultiSelectMode && (
                          <Box
                            sx={{
                              height: "40px",
                              backgroundColor: "background.card",
                              display: "flex",
                              alignItems: "center",
                              padding: "0 8px",
                              borderRadius: "16px 16px 0 0",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                              zIndex: 2,
                            }}
                          >
                            <Checkbox
                              checked={selectedCards.includes(card.id)}
                              onChange={() => handleCardSelect(card.id)}
                              sx={{
                                "& .MuiSvgIcon-root": {
                                  fontSize: 24,
                                },
                              }}
                            />
                          </Box>
                        )}
                        {(drag || edit === card.id || isMultiSelectMode) &&
                          !editCardLayout && (
                            <Box
                              sx={{
                                position: "absolute",
                                top:
                                  edit === card.id || isMultiSelectMode
                                    ? "40px"
                                    : 0,
                                left: 0,
                                right: 0,
                                bottom: 20,
                                zIndex: 10,
                                backgroundColor: "transparent",
                                cursor: "move",
                                pointerEvents: "auto",
                              }}
                            />
                          )}
                        {(drag || edit === card.id || isMultiSelectMode) &&
                          !editCardLayout && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                bottom: 0,
                                width: "calc(100% - 20px)",
                                height: 20,
                                zIndex: 10,
                                backgroundColor: "transparent",
                                cursor: "move",
                                pointerEvents: "auto",
                              }}
                            />
                          )}
                        <Paper
                          elevation={0}
                          onClick={(e) => {
                            if (isEditTitle === card.id) {
                              e.stopPropagation();
                              return;
                            }
                          }}
                          onMouseDown={(e) => {
                            if (drag) {
                              if (
                                e.target === e.currentTarget ||
                                e.target.closest(".card-title-trigger") ||
                                e.target.closest(".automation-title-trigger")
                              ) {
                                return;
                              }
                              e.stopPropagation();
                              return;
                            }
                            if (
                              e.button === 0 &&
                              (e.target === e.currentTarget ||
                                e.target.closest(".card-title-trigger") ||
                                e.target.closest(".automation-title-trigger"))
                            ) {
                              e.stopPropagation();
                              handleLongPressStart(card, card.id, e);
                            }
                          }}
                          onMouseLeave={handleLongPressCancel}
                          onTouchStart={(e) => {
                            if (drag) {
                              if (
                                e.target === e.currentTarget ||
                                e.target.closest(".card-title-trigger") ||
                                e.target.closest(".automation-title-trigger")
                              ) {
                                return;
                              }
                              return;
                            }
                            if (
                              e.target === e.currentTarget ||
                              e.target.closest(".card-title-trigger") ||
                              e.target.closest(".automation-title-trigger")
                            ) {
                              e.stopPropagation();
                              handleLongPressStart(card, card.id, e);
                            }
                          }}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={(e) => {
                            handleLongPressCancel(e);
                          }}
                          sx={{
                            height:
                              edit === card.id || isMultiSelectMode
                                ? "calc(100% - 40px)"
                                : "100%",
                            width: "100%",
                            padding: "8px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            borderRadius:
                              edit === card.id || isMultiSelectMode
                                ? "0 0 16px 16px"
                                : "16px",
                            bgcolor:
                              getCardColor(
                                card?.setting?.backgroundColor,
                                theme.palette.mode
                              ) === "default"
                                ? theme.palette.background.card
                                : getCardColor(
                                    card?.setting?.backgroundColor,
                                    theme.palette.mode
                                  ),
                            overflow: "hidden",
                            boxSizing: "border-box",
                            maxHeight: "100%",
                            maxWidth: "100%",
                            opacity:
                              drag || edit === card.id || isMultiSelectMode
                                ? 0.8
                                : 1,
                            filter: "none",
                            boxShadow: "none",
                            transform:
                              pressingCardId === card.id
                                ? "scale(0.98)"
                                : "scale(1)",
                          }}
                        >
                          <DashboardCard
                            card={card}
                            drag={editCardLayout}
                            isEditing={
                              drag || edit === card.id || isMultiSelectMode
                            }
                            onStateUpdate={handleWidgetUpdateEvent}
                            layoutSize={getCurrentLayoutSize()}
                          ></DashboardCard>
                        </Paper>
                      </div>
                    ))}
                  </ResponsiveGridLayout>
                )}
              </>
            )}
          </Box>
          <EditWidgetModal
            isOpen={!!editCard}
            onClose={() => {
              setEditCard(null);
              setEditCardLayout(false);
              setEdit(null);
            }}
            onSave={() => {
              setEditCard(null);
              setEditCardLayout(false);
              setEdit(null);
            }}
            widgetCard={editCard}
            layouts={layouts}
            cardList={cardList}
            layoutSize={getCurrentLayoutSize()}
            handleLayoutChange={handleUpdateWidgetLayout}
          />
          {/* <TwinTagDialog1
          open={openTwinTagDialog}
          onClose={handleDialogClose}
          widgets={activeWidgets}
          onValueChange={handleUiWidgetValueChange}
        /> */}
          <AddOptionDialog
            open={openOptionDialog}
            onClose={handleCloseOptionDialog}
            onDeviceSelect={handleNewDeviceTwin}
            onWidgetSelect={handleNewWidget}
          />
          <UpsertWidgetDialog
            open={openWidgetDialog}
            currentWidgets={currentWidgets}
            existingNames={widgetNames}
            onClose={handleCloseWidgetDialog}
            onSave={handleUpsertWidgets}
            isUpserting={isUpsertingWidget}
          />
          <UpsertDeviceDialog
            open={openDeviceDialog}
            onClose={handleCloseDeviceDialog}
            onBeforeSave={handleBeforeUpsertDeviceTwin}
            onSave={handleUpsertDeviceTwin}
            currentCanvasId={curCanvasRef.current}
          />
          <DeleteCardDialog
            open={openDeleteDialog}
            card={currentCard}
            onClose={handleCloseDeleteDialog}
            onDelete={handleDeleteCard}
          />
          <EditCardDialog
            open={openCardDialog}
            card={currentCard}
            onEditWidgets={handleEditWidgets}
            onClose={handleCloseCardDialog}
            onSave={handleUpdateCard}
            onDelete={handleDeleteCard}
          />
          <CardContentEditDialog
            open={isEditTitle === currentEditCard?.id}
            card={currentEditCard}
            onClose={() => {
              setIsEditTitle(false);
            }}
            onSave={handleUpdateEditCard}
          />
          {isMultiSelectMode && (
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "background.paper",
                padding: { xs: 1, sm: 2 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 1, sm: 0 },
                boxShadow: "0px -2px 4px rgba(0,0,0,0.1)",
                zIndex: 1000,
              }}
            >
              <Typography
                sx={{
                  textAlign: { xs: "center", sm: "left" },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                {selectedCards.length} cards selected
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedCards([]);
                    setIsMultiSelectMode(false);
                  }}
                  sx={{
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleBatchDelete}
                  disabled={selectedCards.length === 0 || isBatchDeleting}
                  sx={{
                    textTransform: "none",
                  }}
                >
                  Delete
                  {isBatchDeleting && (
                    <CircularProgress size={24} sx={{ ml: 1 }} />
                  )}
                </Button>
              </Box>
            </Box>
          )}
          <BatchDeleteDialog
            open={isBatchDeleteDialogOpen}
            count={selectedCards.length}
            onClose={() => setIsBatchDeleteDialogOpen(false)}
            onDelete={handleBatchDeleteConfirm}
          />
        </Box>
      </>
    );
  }
);
DashboardView.displayName = "DashboardView";
export default DashboardView;
